import React from "react";
import { Info, AlertTriangle } from "lucide-react";
import {
  useImportCapabilities,
  formatBytes,
  formatCount,
} from "../hooks/useImportCapabilities";

/**
 * Renders backend-authoritative upload guardrails so users always see the
 * effective server limits. Backend validation is still the sole authority;
 * this callout is informational only. When the capabilities endpoint fails,
 * we render conservative fallback numbers plus a "using safe defaults"
 * hint so the user can still proceed and the server makes the final call.
 */
export function LimitsCallout() {
  const { caps, loading, error } = useImportCapabilities();

  const entity = caps.supported_entity_types.join(", ") || "contact";
  const exts = caps.accepted_file_extensions.map((e) => e.toUpperCase().replace(".", "")).join(", ");

  return (
    <div
      className="rounded-md border border-slate-200 bg-slate-50 p-3 text-sm"
      data-testid="limits-callout"
      role="note"
    >
      <div className="flex items-start gap-2">
        <Info className="w-4 h-4 mt-0.5 text-slate-500 shrink-0" />
        <div className="w-full">
          <div className="font-medium text-slate-800">File requirements</div>
          <ul
            className="mt-1 space-y-0.5 text-slate-600 text-[13px]"
            data-testid="limits-list"
            data-loading={loading ? "true" : "false"}
          >
            <li data-testid="limits-entity">
              Contact imports only ({entity})
            </li>
            <li data-testid="limits-format">
              {exts} format · UTF-8 · comma-separated · with header row
            </li>
            <li data-testid="limits-size">
              Up to <strong>{formatBytes(caps.max_file_bytes)}</strong> ·{" "}
              <strong>{formatCount(caps.max_rows)}</strong> rows ·{" "}
              <strong>{formatCount(caps.max_columns)}</strong> columns
            </li>
            <li data-testid="limits-cell">
              Individual cell up to {formatCount(caps.max_cell_length)} characters
            </li>
          </ul>
          {error && (
            <div
              className="mt-2 flex items-start gap-1.5 text-xs text-amber-800"
              role="status"
              data-testid="limits-fallback-note"
            >
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>
                Showing safe defaults — couldn&apos;t reach the capabilities service.
                The server will still enforce its real limits on upload.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
